import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Zap, AlertTriangle, TrendingDown, Package, X, Check,
  DollarSign, Bell, ChevronRight
} from 'lucide-react';
import { getInterventions, dismissIntervention, actOnIntervention } from '../services/api';

const typeIcons = {
  procurement_order: Zap,
  price_anomaly: TrendingDown,
  waste_flagged: DollarSign,
  supply_risk: Package
};

const typeColors = {
  procurement_order: 'bg-blue-500',
  price_anomaly: 'bg-amber-500',
  waste_flagged: 'bg-red-500',
  supply_risk: 'bg-purple-500'
};

const urgencyColors = {
  high: 'border-l-red-500',
  medium: 'border-l-amber-500',
  low: 'border-l-slate-300'
};

function formatCurrency(value) {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  return `$${value?.toFixed(0) || 0}`;
}

function InterventionCard({ intervention, onDismiss, onAct }) {
  const Icon = typeIcons[intervention.type] || AlertTriangle;
  const color = typeColors[intervention.type] || 'bg-slate-500';
  
  return (
    <div 
      className={`card-premium p-5 border-l-4 ${urgencyColors[intervention.urgency]}`}
      data-testid={`intervention-${intervention.id}`}
    >
      <div className="flex items-start gap-4">
        <div className={`w-10 h-10 ${color} rounded-lg flex items-center justify-center flex-shrink-0`}>
          <Icon className="w-5 h-5 text-white" />
        </div>
        
        <div className="flex-1">
          <div className="flex items-start justify-between mb-2">
            <div>
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                intervention.urgency === 'high' ? 'bg-red-100 text-red-700' :
                intervention.urgency === 'medium' ? 'bg-amber-100 text-amber-700' :
                'bg-slate-100 text-slate-600'
              }`}>
                {intervention.urgency.toUpperCase()} PRIORITY
              </span>
              <h3 className="font-semibold text-slate-900 mt-2">{intervention.drug_name}</h3>
            </div>
            {intervention.potential_savings > 0 && (
              <div className="text-right">
                <p className="text-xs text-slate-500">Potential Savings</p>
                <p className="text-lg font-bold text-emerald-600">
                  {formatCurrency(intervention.potential_savings)}
                </p>
              </div>
            )}
          </div>
          
          <p className="text-sm text-slate-600 mb-4">{intervention.message}</p>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => onAct(intervention.id)}
              className="btn-primary text-sm py-1.5 flex items-center gap-1"
            >
              <Check className="w-4 h-4" />
              Take Action
            </button>
            <button
              onClick={() => onDismiss(intervention.id)}
              className="btn-ghost text-sm py-1.5 flex items-center gap-1"
            >
              <X className="w-4 h-4" />
              Dismiss
            </button>
            {intervention.drug_id && (
              <Link
                to={`/drug/${intervention.drug_id}`}
                className="text-sm text-[#0A2540] hover:underline ml-auto flex items-center gap-1"
              >
                View Details <ChevronRight className="w-3 h-3" />
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function InterventionsPage() {
  const [interventions, setInterventions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('active');

  useEffect(() => {
    fetchInterventions();
  }, [filter]);

  const fetchInterventions = async () => {
    setLoading(true);
    try {
      const res = await getInterventions(filter);
      setInterventions(res.data.interventions || []);
    } catch (error) {
      console.error('Failed to fetch interventions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = async (id) => {
    try {
      await dismissIntervention(id);
      setInterventions(prev => prev.filter(i => i.id !== id));
    } catch (error) {
      console.error('Failed to dismiss:', error);
    }
  };

  const handleAct = async (id) => {
    try {
      await actOnIntervention(id);
      setInterventions(prev => prev.filter(i => i.id !== id));
    } catch (error) {
      console.error('Failed to act:', error);
    }
  };

  const counts = {
    high: interventions.filter(i => i.urgency === 'high').length,
    medium: interventions.filter(i => i.urgency === 'medium').length,
    total: interventions.length
  };

  const totalPotentialSavings = interventions.reduce((sum, i) => sum + (i.potential_savings || 0), 0);

  if (loading) {
    return (
      <div className="p-6" data-testid="interventions-loading">
        <div className="skeleton h-8 w-48 mb-6" />
        <div className="space-y-4">
          {[1, 2, 3].map(i => <div key={i} className="skeleton h-32 rounded-xl" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="interventions-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Real-Time Interventions</h1>
          <p className="text-slate-500">Alerts and opportunities requiring immediate attention</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-sm text-slate-500">Potential Savings</p>
            <p className="text-xl font-bold text-emerald-600">{formatCurrency(totalPotentialSavings)}</p>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="card-premium p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
            <Bell className="w-5 h-5 text-slate-500" />
          </div>
          <div>
            <p className="text-xl font-bold text-slate-900">{counts.total}</p>
            <p className="text-xs text-slate-500">Active Alerts</p>
          </div>
        </div>
        <div className="card-premium p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <p className="text-xl font-bold text-slate-900">{counts.high}</p>
            <p className="text-xs text-slate-500">High Priority</p>
          </div>
        </div>
        <div className="card-premium p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <Zap className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <p className="text-xl font-bold text-slate-900">
              {interventions.filter(i => i.type === 'procurement_order').length}
            </p>
            <p className="text-xs text-slate-500">Procurement</p>
          </div>
        </div>
        <div className="card-premium p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
            <TrendingDown className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <p className="text-xl font-bold text-slate-900">
              {interventions.filter(i => i.type === 'price_anomaly').length}
            </p>
            <p className="text-xs text-slate-500">Price Anomalies</p>
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex items-center gap-2 mb-6 border-b border-slate-200">
        {['active', 'acted', 'dismissed'].map(tab => (
          <button
            key={tab}
            onClick={() => setFilter(tab)}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors capitalize ${
              filter === tab 
                ? 'border-[#0A2540] text-[#0A2540]' 
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Interventions List */}
      <div className="space-y-4">
        {interventions.map(intervention => (
          <InterventionCard
            key={intervention.id}
            intervention={intervention}
            onDismiss={handleDismiss}
            onAct={handleAct}
          />
        ))}
        
        {interventions.length === 0 && (
          <div className="card-premium p-12 text-center">
            <Bell className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600 font-medium">No {filter} interventions</p>
            <p className="text-sm text-slate-500 mt-1">
              {filter === 'active' ? 'All caught up! No alerts require your attention.' : `No ${filter} interventions found.`}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
